// AuthContext - Authentication State Management
// Phase 4: With Subscription Support
// © 2025 precatio, llc

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const API_BASE = '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('precat_token'));
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        
        // Also fetch subscription status
        fetchSubscriptionStatus();
      } else {
        // Token is invalid or expired
        localStorage.removeItem('precat_token');
        setToken(null);
        setUser(null);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/subscription/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Update user with subscription info
        setUser(prev => prev ? {
          ...prev,
          subscriptionStatus: data.status,
          subscriptionTier: data.tier,
          trialDaysRemaining: data.trialDaysRemaining
        } : null);
      }
    } catch (error) {
      // Subscription endpoint might not exist yet - that's ok
      console.log('Subscription status not available');
    }
  };

  const signup = async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Signup failed');
    }

    // Save token and user
    localStorage.setItem('precat_token', data.token);
    setToken(data.token);
    setUser(data.user);

    return data;
  };

  const login = async (email, password) => {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Login failed');
    }

    // Save token and user
    localStorage.setItem('precat_token', data.token);
    setToken(data.token);
    setUser(data.user);

    // Fetch subscription status after login
    setTimeout(() => fetchSubscriptionStatus(), 100);

    return data;
  };

  const logout = async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local state regardless of API response
    localStorage.removeItem('precat_token');
    setToken(null);
    setUser(null);
  };

  const forgotPassword = async (email) => {
    const response = await fetch(`${API_BASE}/auth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Request failed');
    }

    return data;
  };

  const resetPassword = async (resetToken, newPassword) => {
    const response = await fetch(`${API_BASE}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token: resetToken, newPassword })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Reset failed');
    }

    return data;
  };

  // Link anonymous conversation to user account
  const linkConversation = async (sessionId) => {
    if (!token) return;

    try {
      await fetch(`${API_BASE}/conversation/link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessionId })
      });
    } catch (error) {
      console.error('Error linking conversation:', error);
    }
  };

  // Check if user has premium access (trial or paid)
  const hasPremiumAccess = useCallback(() => {
    if (!user) return false;
    
    const { subscriptionStatus, trialDaysRemaining } = user;
    
    if (subscriptionStatus === 'active' || subscriptionStatus === 'premium') {
      return true;
    }
    
    if (subscriptionStatus === 'trial' && trialDaysRemaining > 0) {
      return true;
    }
    
    return false;
  }, [user]);

  // Check if user has access to a specific tier's features
  const hasTierAccess = useCallback((requiredTier) => {
    if (!user) return false;
    
    const tierHierarchy = {
      pilgrim: 0,
      disciple: 1,
      companion: 2,
      retreatant: 3
    };
    
    const userTier = user.subscriptionTier || 'pilgrim';
    
    // If in trial, they have disciple access
    if (user.subscriptionStatus === 'trial' && user.trialDaysRemaining > 0) {
      return tierHierarchy['disciple'] >= tierHierarchy[requiredTier];
    }
    
    // Otherwise check their actual tier
    return tierHierarchy[userTier] >= tierHierarchy[requiredTier];
  }, [user]);

  // Refresh subscription status
  const refreshSubscription = useCallback(() => {
    if (token) {
      fetchSubscriptionStatus();
    }
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin === 1,
    hasPremiumAccess,
    hasTierAccess,
    refreshSubscription,
    signup,
    login,
    logout,
    forgotPassword,
    resetPassword,
    linkConversation,
    refreshUser: fetchCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
